import React from 'react';
import * as Yup from 'yup';
import { LoadingButton } from '@mui/lab';
import { Alert, Button, Typography } from '@mui/material';
import { Field, Form, Formik, FormikHelpers } from 'formik';
import { TextField } from 'components/formik/text-field';
import { useDialog } from 'components/layouts/page-with-dialog/dialog-context';
import { useUpdateUserTelegramMutation } from 'app/store';
import { immediateToast } from 'utils/toast';
import { handleServerErrors } from 'utils/utils';
import useProfile from 'hooks/use-update-profile-query';

type TelegramType = {
    telegram: string;
};

const validationSchema = Yup.object().shape({
    telegram: Yup.string()
        .matches(/^[0-9]*$/, 'شناسه تلگرام فقط باید شامل عدد باشد')
        .max(15, 'شناسه تلگرام باید حداکثر 15 رقم باشد'),
});

const TelegramIDNotificationForm = (): JSX.Element => {
    const [updateTelegram, { isLoading: isUpdating }] =
        useUpdateUserTelegramMutation();
    const { closeDialog } = useDialog();
    const { profile, update } = useProfile();

    const handleSubmit = (
        values: TelegramType,
        { setStatus, setSubmitting }: FormikHelpers<TelegramType>,
    ) => {
        if (!profile) {
            setSubmitting(false);
            return;
        }

        const telegram = values.telegram?.trim() || '';

        updateTelegram({ telegram })
            .unwrap()
            .then(() => {
                immediateToast('success', {
                    message: telegram
                        ? 'شناسه تلگرام بروزرسانی شد'
                        : 'شناسه تلگرام حذف شد',
                    timeout: 2000,
                });
                update({
                    ...profile,
                    telegram_id: telegram,
                });
                closeDialog('set-telegram-id');
            })
            .catch((error) => {
                handleServerErrors(error, setStatus);
            })
            .finally(() => {
                setSubmitting(false);
            });
    };

    return (
        <>
            <Alert
                variant="outlined"
                severity="info"
                sx={{ mb: 3, display: 'flex', alignItems: 'center' }}>
                <Typography variant="body1" component="div" fontSize={14}>
                    جهت
                    <Button
                        variant="contained"
                        color="primary"
                        size="small"
                        sx={{
                            mx: 0.5,
                            display: 'inline-flex',
                            verticalAlign: 'middle',
                        }}>
                        فعالسازی ربات
                    </Button>
                    ابتدا ربات را استارت کرده و پس از دریافت شناسه، شناسه حساب
                    تلگرامی خود را در فیلد زیر وارد کنید.
                </Typography>
            </Alert>

            <Formik
                initialValues={{ telegram: profile?.telegram_id || '' }}
                enableReinitialize
                onSubmit={handleSubmit}
                validationSchema={validationSchema}
                validateOnChange={false}
                validateOnBlur={false}>
                {({ handleSubmit, isSubmitting, errors }) => (
                    <Form onSubmit={handleSubmit}>
                        <Field
                            name="telegram"
                            component={TextField}
                            label="شناسه عددی تلگرام"
                            fullWidth
                            numericOnly
                            maxLength={15}
                            inputProps={{
                                inputMode: 'numeric',
                                pattern: '[0-9]*',
                            }}
                            error={Boolean(errors?.telegram)}
                            helperText={errors?.telegram}
                            ltr="true"
                            icon={
                                <i className="fa-light fa-paper-plane"></i>
                            }
                        />

                        <LoadingButton
                            fullWidth
                            variant="contained"
                            sx={{ mt: 2 }}
                            type="submit"
                            loading={isSubmitting || isUpdating}>
                            ذخیره
                        </LoadingButton>
                    </Form>
                )}
            </Formik>
        </>
    );
};

export default TelegramIDNotificationForm;
